import {calculateErrorMessage} from 'common/helpers'

import {
  applyBoardMappingToBody,
  resolveMenuPathForApi,
} from '../../../../app/pages/chatbot/components/config/chatbotModeConfig'
import {sanitizePageContext} from '../../../../app/pages/chatbot/pageContext/sanitizePageContext'
import {getActiveSession, getTenantDomain} from '../../../../common/HelperFunctions'
import constants, {SECRET_KEY, base_url} from '../../../../common/constants'
import store from '../../Store'
import {CALL_BACKEND_API} from '../ApiActionTypes'
import {SEND_CHAT_MESSAGE} from './ActionTypes/ApiActionTypes'
import {
  REDUX_ADD_BOT_MESSAGE,
  REDUX_ADD_CONVERSATION_HISTORY,
  REDUX_ADD_USER_MESSAGE,
  REDUX_CLEAR_CHAT,
  REDUX_RESET_MENU_STATE,
  REDUX_RESTORE_CHAT_SESSION,
  REDUX_SET_MENU_STATE,
  REDUX_SET_MESSAGE_UNDERSTOOD_AS,
  REDUX_SET_SELECTED_MENU_PATH,
  REDUX_SET_TYPING,
  REDUX_UPDATE_MESSAGE_STATUS,
} from './ActionTypes/ReduxActionTypes'

export function cleanupAllAudio() {
  stopFillerAudio()
  stopFullResponseAudio()
}

export function handleStreamingAudioError() {
  cleanupAllAudio()
}

export function decodeAnswerText(answerText) {
  if (!answerText) return ''
  try {
    return decodeURIComponent(answerText)
  } catch {
    return answerText
  }
}

export function parseWalkthroughData(xHasWalkthrough, xWalkthroughData) {
  if (!xHasWalkthrough || xHasWalkthrough === 'false') return null
  if (!xWalkthroughData) return null
  try {
    return typeof xWalkthroughData === 'string' ? JSON.parse(xWalkthroughData) : xWalkthroughData
  } catch {
    return null
  }
}

let currentFullAudio = null
let currentFullUrl = null
export function playFullResponseAudio(audioBytes, mimeType = 'audio/mpeg', onEnded, onError) {
  stopFillerAudio()
  stopFullResponseAudio()
  if (!audioBytes) return
  const blob = new Blob([audioBytes], {type: mimeType})
  const url = URL.createObjectURL(blob)
  const audio = new Audio(url)
  audio.onended = () => {
    stopFullResponseAudio()
    if (onEnded) onEnded()
  }
  audio.onerror = (e) => {
    stopFullResponseAudio()
    if (onError) onError(e)
  }
  audio.play().catch((e) => {
    if (onError) onError(e)
  })
  currentFullAudio = audio
  currentFullUrl = url
}

export function stopFullResponseAudio() {
  if (currentFullAudio) {
    try {
      currentFullAudio.pause()
    } catch (e) {
      calculateErrorMessage(e)
    }
    currentFullAudio = null
  }
  if (currentFullUrl) {
    try {
      URL.revokeObjectURL(currentFullUrl)
    } catch (e) {
      calculateErrorMessage(e)
    }
    currentFullUrl = null
  }
}

let currentFillerAudio = null
let currentFillerUrl = null
export function playFillerAudio(audioBytes, mimeType = 'audio/mpeg') {
  stopFillerAudio()
  if (!audioBytes) return
  const blob = audioBytes instanceof Blob ? audioBytes : new Blob([audioBytes], {type: mimeType})
  const url = URL.createObjectURL(blob)
  const audio = new Audio(url)
  audio.play().catch(() => {})
  currentFillerAudio = audio
  currentFillerUrl = url
}

export function stopFillerAudio() {
  if (currentFillerAudio) {
    try {
      currentFillerAudio.pause()
    } catch (e) {
      calculateErrorMessage(e)
    }
    currentFillerAudio = null
  }
  if (currentFillerUrl) {
    try {
      URL.revokeObjectURL(currentFillerUrl)
    } catch (e) {
      calculateErrorMessage(e)
    }
    currentFillerUrl = null
  }
}

export function parseHeaders(headerBytes) {
  if (!headerBytes) return {}
  let headerStr = ''
  try {
    headerStr = new TextDecoder('iso-8859-1').decode(headerBytes)
  } catch {
    headerStr = new TextDecoder().decode(headerBytes)
  }
  const headers = {}
  for (const line of headerStr.split(/\r?\n/)) {
    const idx = line.indexOf(':')
    if (idx === -1) continue
    const key = line.slice(0, idx).trim().toLowerCase()
    const value = line.slice(idx + 1).trim()
    headers[key] = value
  }
  if (headers['x-audio-type']) headers.xAudioType = headers['x-audio-type']
  if (headers['x-answer-text']) headers.xAnswerText = headers['x-answer-text']
  if (headers['x-has-walkthrough']) headers.xHasWalkthrough = headers['x-has-walkthrough']
  if (headers['x-walkthrough-data']) headers.xWalkthroughData = headers['x-walkthrough-data']
  if (headers['x-understood-as']) headers.xUnderstoodAs = headers['x-understood-as']
  return headers
}

export function parseMultipartResponseBetter(buffer, boundary) {
  if (!buffer || !boundary) return []
  const enc = new TextEncoder()
  const boundaryBytes = enc.encode(boundary)
  const doubleCRLF = enc.encode('\r\n\r\n')
  const parts = []
  let pos = 0
  function indexOf(haystack, needle, start) {
    for (let i = start; i <= haystack.length - needle.length; i++) {
      let match = true
      for (let j = 0; j < needle.length; j++) {
        if (haystack[i + j] !== needle[j]) {
          match = false
          break
        }
      }
      if (match) return i
    }
    return -1
  }
  while (pos < buffer.length) {
    let boundaryIdx = indexOf(buffer, boundaryBytes, pos)
    if (boundaryIdx === -1) break
    let nextBoundaryIdx = indexOf(buffer, boundaryBytes, boundaryIdx + boundaryBytes.length)
    if (nextBoundaryIdx === -1) nextBoundaryIdx = buffer.length
    let partStart = boundaryIdx + boundaryBytes.length
    if (buffer[partStart] === 13 && buffer[partStart + 1] === 10) partStart += 2
    let partEnd = nextBoundaryIdx
    let headerEnd = indexOf(buffer, doubleCRLF, partStart)
    if (headerEnd === -1 || headerEnd + doubleCRLF.length > partEnd) break
    let headers = buffer.slice(partStart, headerEnd)
    let body = buffer.slice(headerEnd + doubleCRLF.length, partEnd - 2)
    parts.push({headers, body})
    pos = nextBoundaryIdx
  }
  return parts
}

export function extractBoundary(response) {
  if (!response || !response.headers) return null
  const contentType =
    response.headers.get('Content-Type') || response.headers.get('content-type') || ''
  const match = contentType.match(/boundary=([^"]+|"[^"]+")/i)
  if (!match) return null
  let boundary = match[1]
  if (boundary.startsWith('"') && boundary.endsWith('"')) {
    boundary = boundary.slice(1, -1)
  }
  if (!boundary.startsWith('--')) {
    boundary = '--' + boundary
  }
  return boundary
}

export async function readStreamToBuffer(stream) {
  const reader = stream.getReader()
  let chunks = []
  let totalLength = 0
  try {
    while (true) {
      const {done, value} = await reader.read()
      if (done) break
      if (value) {
        chunks.push(value)
        totalLength += value.length || value.byteLength || 0
      }
    }
  } catch (err) {
    calculateErrorMessage(err)
  }
  let buffer = new Uint8Array(totalLength)
  let offset = 0
  for (const chunk of chunks) {
    buffer.set(chunk, offset)
    offset += chunk.length || chunk.byteLength || 0
  }
  return buffer
}

export function isStreamingAudioResponse(response) {
  if (!response || !response.headers) return false
  const contentType =
    response.headers.get('Content-Type') || response.headers.get('content-type') || ''
  if (contentType.toLowerCase().startsWith('multipart/x-mixed-replace')) {
    return true
  }
  const processingStatus =
    response.headers.get('X-Processing-Status') || response.headers.get('x-processing-status')
  if (processingStatus && processingStatus.toLowerCase() === 'streaming') {
    return true
  }
  return false
}

export const addUserMessage = (message) => {
  return {
    type: REDUX_ADD_USER_MESSAGE,
    payload: {message},
  }
}

export const addBotMessage = (message) => {
  return {
    type: REDUX_ADD_BOT_MESSAGE,
    payload: {message},
  }
}

export const setTyping = (isTyping) => {
  return {
    type: REDUX_SET_TYPING,
    payload: {isTyping},
  }
}

export const clearChat = () => {
  return {
    type: REDUX_CLEAR_CHAT,
    payload: {},
  }
}

export const updateMessageStatus = (messageId, status) => {
  return {
    type: REDUX_UPDATE_MESSAGE_STATUS,
    payload: {messageId, status},
  }
}

/**
 * What the server understood a non-English question to be, attached to the
 * user's own message so it is shown next to the words they actually typed.
 *
 * Only ever set when the question was NOT English — an English turn carries no
 * understoodAs and the bubble renders exactly as it did before.
 *
 * @param {string} messageId
 * @param {{query: string, original: string, dateRange: object|null, entities: string[]}} understoodAs
 */
export const setMessageUnderstoodAs = (messageId, understoodAs) => {
  return {
    type: REDUX_SET_MESSAGE_UNDERSTOOD_AS,
    payload: {messageId, understoodAs},
  }
}

export const removeChatMessage = (messageId) => {
  return {
    type: 'REDUX_REMOVE_CHAT_MESSAGE',
    payload: {messageId},
  }
}

export const addConversationHistory = (historyItem) => {
  return {
    type: REDUX_ADD_CONVERSATION_HISTORY,
    payload: {
      query: historyItem?.query,
      answer: historyItem?.answer,
    },
  }
}

export const setMenuState = (menuState) => {
  return {
    type: REDUX_SET_MENU_STATE,
    payload: menuState,
  }
}

export const resetMenuState = () => {
  return {
    type: REDUX_RESET_MENU_STATE,
  }
}

export const setSelectedMenuPath = (path) => {
  return {
    type: REDUX_SET_SELECTED_MENU_PATH,
    payload: path,
  }
}

export const restoreChatSession = (payload) => {
  return {
    type: REDUX_RESTORE_CHAT_SESSION,
    payload,
  }
}

/**
 * @param {string} query
 * @param {Array<{query: string, answer: string, attendanceContext?: object}>|null|undefined} conversationHistory
 * @param {string|null|undefined} menuSelectedPath
 * @param {(response: unknown) => void} onSuccess
 * @param {(error: unknown) => void} onFailure
 * @param {'general'|'attendance'|string} [chatbotMode]
 * @param {{attendanceContext?: object, disambiguationPick?: object, capabilityId?: string, pendingClarification?: object, pageContext?: {surface: string, path: string}}|null|undefined} [requestOptions]
 */
export const sendChatMessage = (
  query,
  conversationHistory,
  menuSelectedPath,
  onSuccess,
  onFailure,
  chatbotMode = 'general',
  requestOptions = null
) => {
  const base =
    conversationHistory && conversationHistory.length > 0
      ? {
          query,
          conversationHistory: conversationHistory
            .map((item) => {
              const maybe = item?.historyItem || item || {}
              const q =
                typeof maybe.query === 'string'
                  ? maybe.query
                  : maybe.query
                    ? String(maybe.query)
                    : ''
              const a =
                typeof maybe.answer === 'string'
                  ? maybe.answer
                  : maybe.answer
                    ? String(maybe.answer)
                    : ''
              const entry = {query: q, answer: a}
              if (maybe.attendanceContext && typeof maybe.attendanceContext === 'object') {
                entry.attendanceContext = maybe.attendanceContext
              }
              // The capability the backend attributed the turn to, replayed so follow-ups bind
              // on that attribution instead of the backend sniffing the answer for a marker.
              if (typeof maybe.capability === 'string' && maybe.capability.trim()) {
                entry.capability = maybe.capability.trim()
              }
              // The clarification that turn asked, kept in the replay so the reply to it binds
              // to the right question even if the backend's per-user memory of it is gone.
              if (
                maybe.clarification &&
                typeof maybe.clarification === 'object' &&
                Array.isArray(maybe.clarification.options)
              ) {
                entry.clarification = maybe.clarification
              }
              return entry
            })
            .filter((it) => it.query.trim() !== '' && it.answer.trim() !== ''),
        }
      : {query}

  let body = {...base}
  // The unified assistant names no board: the backend routes every turn itself from the
  // history and ignores `query_type` / `board` / `subType` (menu_selected was retired with the
  // single surface). Only the menu-driven general bot still maps its menu path onto a board.
  if (chatbotMode !== 'assistant') {
    const effectiveMenuPath = resolveMenuPathForApi(chatbotMode, menuSelectedPath)
    applyBoardMappingToBody(body, effectiveMenuPath)
  }

  if (requestOptions && typeof requestOptions === 'object') {
    const options = {}
    if (requestOptions.attendanceContext && typeof requestOptions.attendanceContext === 'object') {
      options.attendanceContext = requestOptions.attendanceContext
    }
    if (
      requestOptions.disambiguationPick &&
      typeof requestOptions.disambiguationPick === 'object'
    ) {
      options.disambiguationPick = requestOptions.disambiguationPick
    }
    // A capability the user picked explicitly (a clarification chip, the first-open capability
    // list). The backend revalidates it against its registry and re-applies the permission clamp,
    // so this steers a turn without asserting anything; it replaces the retired `menu_selected`.
    if (typeof requestOptions.capabilityId === 'string' && requestOptions.capabilityId.trim()) {
      options.capabilityId = requestOptions.capabilityId.trim()
    }
    // The previous turn's clarification, echoed back verbatim. The backend is stateless per turn:
    // without this, the answer to a question we asked cannot be bound to it. Sent exactly as it
    // was received — enriching it with resolved ids or scope would be pointless (the backend
    // discards those and re-probes) and this round trip is not an authorisation channel.
    if (
      requestOptions.pendingClarification &&
      typeof requestOptions.pendingClarification === 'object'
    ) {
      options.pendingClarification = requestOptions.pendingClarification
    }
    // The page the message was sent from. The backend scopes the turn on `surface`
    // (`admission_admin`; `ai_help` only says which screen, the help bot's scope is its own). `path` must be a pathname only — a query string can
    // carry applicant ids — and `page` / `visible` / `dom` are whitelisted ids and short labels,
    // capped here whatever the caller built.
    const pageContext = sanitizePageContext(requestOptions.pageContext)
    if (pageContext) {
      options.pageContext = pageContext
    }
    if (Object.keys(options).length > 0) {
      body.options = options
    }
  }

  return {
    type: CALL_BACKEND_API,
    payload: {
      apiUrl: constants.api_chatbot_chat,
      getString: '',
      useBaseURL: true,
      requestType: 'POST',
      header: 'application/json',
      body,
      reduxActionType: SEND_CHAT_MESSAGE,
      onSuccess: (response) => {
        if (onSuccess) onSuccess(response)
      },
      onFailure: (error) => {
        if (onFailure) onFailure(error)
      },
    },
  }
}

/**
 * Versi for admission applicants (`/admissions/dashboard`, `/admissions/apply`). Same request body
 * and response contract as `sendChatMessage` — built by it — sent to `admission/chat` instead of
 * `chatbot/chat`. Goes through the same saga, which never logs an admission user out on a 401.
 *
 * @param {Parameters<typeof sendChatMessage>} args
 */
export const sendAdmissionApplicantChatMessage = (...args) => {
  const action = sendChatMessage(...args)
  return {...action, payload: {...action.payload, apiUrl: constants.api_admission_chat}}
}

/**
 * Headers for the public admission chat: the same tenant/platform headers as the public
 * admission signup/design calls (`pages/admissions/lib/api.ts`), and never an Authorization
 * header — the endpoint is for visitors, and a staff token opening `/admissions` must not go.
 */
export const buildAdmissionPublicChatHeaders = () => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  platform: 'web',
  platformKey: SECRET_KEY.web,
  version: '1.0.0',
  domain: getTenantDomain(),
})

const isSuccessfulChatBody = (json) =>
  json != null &&
  (json.status === 'true' ||
    json.status === true ||
    json.success === 'true' ||
    json.success === true)

/**
 * Versi on the public admission pages, for anyone who is not a signed-in applicant (signed out,
 * or signed in as staff). Same arguments, body and response contract as `sendChatMessage`, sent
 * to `admission/public-chat`.
 *
 * Deliberately NOT a saga action: the saga attaches the stored token, stops every request while a
 * logout is pending, and can bounce the tab to /logout on a 401. None of that belongs to a
 * visitor's request. `onSuccess` gets the parsed body exactly as the saga would hand it over;
 * `onFailure` gets that body with `statusCode` filled from the HTTP status when missing (so 403
 * and 429 are readable), or the thrown error for a network failure.
 *
 * @param {Parameters<typeof sendChatMessage>} args
 * @returns {Promise<void>}
 */
export const sendAdmissionPublicChatMessage = (...args) => {
  const onSuccess = args[3]
  const onFailure = args[4]
  const {body} = sendChatMessage(...args).payload

  return (async () => {
    let response
    try {
      response = await fetch(`${base_url}${constants.api_admission_public_chat}`, {
        method: 'POST',
        headers: buildAdmissionPublicChatHeaders(),
        body: JSON.stringify(body),
      })
    } catch (error) {
      if (onFailure) onFailure(error)
      return
    }

    let json = null
    try {
      json = await response.json()
    } catch {
      // non-JSON body (proxy error page)
    }

    if (response.ok && isSuccessfulChatBody(json)) {
      if (onSuccess) onSuccess(json)
      return
    }
    if (onFailure) {
      onFailure({...(json ?? {}), statusCode: json?.statusCode ?? response.status})
    }
  })()
}

const safeDecodeHeader = (value) => {
  if (!value) {
    return ''
  }

  const stringValue = String(value)

  try {
    const processed = stringValue.replace(/\+/g, '%20')
    const decoded = decodeURIComponent(processed)

    return decoded
  } catch {
    try {
      const decoded = decodeURIComponent(stringValue)
      return decoded
    } catch {
      return stringValue.replace(/%20/g, ' ').replace(/\+/g, ' ')
    }
  }
}

const filenameForMime = (mimeType) => {
  const t = String(mimeType || '').toLowerCase()
  if (t.includes('wav')) return 'voice-message.wav'
  if (t.includes('mpeg') || t.includes('mp3')) return 'voice-message.mp3'
  if (t.includes('ogg')) return 'voice-message.ogg'
  if (t.includes('mp4') || t.includes('m4a')) return 'voice-message.m4a'
  if (t.includes('webm')) return 'voice-message.webm'
  return 'voice-message.wav'
}

export const fetchRecommendedVoices = async (signal) => {
  const state = store.getState?.() || {}
  const accessToken = state?.userReducer?.accessToken

  const headers = {
    Accept: 'application/json, text/plain, */*',
    domain: getTenantDomain(),
    platform: 'web',
    platformKey: SECRET_KEY.web,
    version: '1.0.0',
  }

  const activeSession = getActiveSession()
  if (activeSession?.db_postfix) {
    headers['session-postfix'] = activeSession.db_postfix
  }

  const branchId = localStorage.getItem('branch')
  if (branchId) headers['branch-id'] = branchId

  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  const response = await fetch(`${base_url}${constants.api_chatbot_recommended_voices}`, {
    method: 'GET',
    headers,
    signal,
  })

  if (!response.ok) {
    let message = `API Error: ${response.status}`
    try {
      const json = await response.json()
      message = json?.message || json?.error || message
    } catch {
      try {
        const text = await response.text()
        if (text) message = text
      } catch (e) {
        calculateErrorMessage(e)
      }
    }

    throw new Error(message)
  }

  const json = await response.json()
  return json?.result || json
}

/**
 * Transcribes a recording without asking the chatbot anything.
 *
 * Unlike `sendVoiceChatMessage` this has no side effects — it does not generate an answer, synthesise
 * speech, or run the query. That separation is what lets dictation put the text in the composer for
 * the user to read and edit before anything acts on it.
 *
 * @param {Blob} audioBlob - Recorded audio; WAV is what the recorder produces and what Azure prefers.
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<string>} The transcribed text, or '' when nothing was recognised.
 */
export const transcribeVoiceRecording = async (audioBlob, opts = {}) =>
  (await transcribeVoiceRecordingDetailed(audioBlob, opts)).text

/**
 * `transcribeVoiceRecording`, with what the server knows about the transcript.
 *
 * @param {Blob} audioBlob
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{text: string, rawText: string|null, detectedLanguage: string|null,
 *   confidence: number|null, lowConfidence: boolean, transcriptId: string|null}>}
 *
 * `text` is always in Latin letters: English, French and Turkish come back as recognised, and
 * Urdu, Hindi and Arabic are rewritten in Roman by the server so the user can read them back.
 * `detectedLanguage` is what was SPOKEN, decided by the server — never re-derive it from `text`,
 * which is Latin for every language by the time it arrives. `confidence` is 0..1, or null when
 * the recogniser does not score its output, and `lowConfidence` is the server's own verdict on
 * it: true means show the text but ask the user to check it.
 */
export const transcribeVoiceRecordingDetailed = async (audioBlob, {signal} = {}) => {
  const formData = new FormData()
  const mimeType = audioBlob?.type || 'audio/wav'
  formData.append('audio', audioBlob, filenameForMime(mimeType))
  // THE AUDIO AND NOTHING ELSE. No language is sent — not even as a hint. The UI language is what
  // the user reads menus in, not what they speak, and sending it made the recogniser decode Urdu
  // as English and return confident English-looking nonsense. The server detects the language
  // from the audio and reports it back as `detectedLanguage`.

  const state = store.getState?.() || {}
  const accessToken = state?.userReducer?.accessToken

  const headers = {
    Accept: 'application/json, text/plain, */*',
    domain: getTenantDomain(),
    platform: 'web',
    platformKey: SECRET_KEY.web,
    version: '1.0.0',
  }

  const activeSession = getActiveSession()
  if (activeSession?.db_postfix) {
    headers['session-postfix'] = activeSession.db_postfix
  }

  const branchId = localStorage.getItem('branch')
  if (branchId) headers['branch-id'] = branchId

  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  const response = await fetch(`${base_url}${constants.api_chatbot_voice_transcribe}`, {
    method: 'POST',
    headers,
    body: formData,
    signal,
  })

  if (!response.ok) {
    let message = `API Error: ${response.status}`
    try {
      const json = await response.json()
      message = json?.message || json?.error || message
    } catch {
      try {
        const text = await response.text()
        if (text) message = text
      } catch (e) {
        calculateErrorMessage(e)
      }
    }

    throw new Error(message)
  }

  const json = await response.json()
  // `language` on the response is only an echo of the hint that was sent, not the detected
  // language — ignore it; `detectedLanguage` is what was heard.
  const result = json?.result || json || {}
  const confidence = Number(result.confidence)
  return {
    text: String(result.text || '').trim(),
    rawText: typeof result.rawText === 'string' ? result.rawText : null,
    detectedLanguage: typeof result.detectedLanguage === 'string' ? result.detectedLanguage : null,
    confidence:
      result.confidence === null || result.confidence === undefined || !Number.isFinite(confidence)
        ? null
        : confidence,
    transcriptId: typeof result.transcriptId === 'string' ? result.transcriptId : null,
  }
}

export const sendVoiceChatMessage = async (
  audioBlob,
  {signal, conversationHistory, voice, menuSelectedPath, chatbotMode = 'general'} = {}
) => {
  const formData = new FormData()
  const mimeType = audioBlob?.type || 'audio/webm'
  formData.append('audio', audioBlob, filenameForMime(mimeType))

  // No language is sent here either, for the same reason as the transcribe call above: the
  // recogniser detects it from the audio, and the answer comes back in English whatever was
  // spoken.

  let filtered = []
  if (Array.isArray(conversationHistory) && conversationHistory.length > 0) {
    filtered = conversationHistory
      .map((it) => ({query: String(it.query || ''), answer: String(it.answer || '')}))
      .filter((it) => it.query.trim() !== '' && it.answer.trim() !== '')
    if (filtered.length > 0) {
      formData.append('conversationHistory', JSON.stringify(filtered))
    }
  }

  if (voice) {
    try {
      formData.append('voice', String(voice))
    } catch (e) {
      calculateErrorMessage(e)
    }
  }

  const effectiveMenuPath = resolveMenuPathForApi(chatbotMode, menuSelectedPath)
  if (effectiveMenuPath) {
    const voiceBody = {}
    applyBoardMappingToBody(voiceBody, effectiveMenuPath)
    if (voiceBody.query_type) {
      formData.append('query_type', String(voiceBody.query_type))
    }
    if (voiceBody.board) {
      formData.append('board', String(voiceBody.board))
    }
    if (voiceBody.subType) {
      formData.append('subType', String(voiceBody.subType))
    }
  }

  const state = store.getState?.() || {}
  const accessToken = state?.userReducer?.accessToken

  const headers = {
    Accept: 'audio/mpeg,application/json;q=0.9,*/*;q=0.8',
    domain: getTenantDomain(),
    platform: 'web',
    platformKey: SECRET_KEY.web,
    version: '1.0.0',
  }

  const activeSession = getActiveSession()
  if (activeSession?.db_postfix) {
    headers['session-postfix'] = activeSession.db_postfix
  }

  const branchId = localStorage.getItem('branch')
  if (branchId) headers['branch-id'] = branchId

  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  const response = await fetch(`${base_url}${constants.api_chatbot_voice_chat}`, {
    method: 'POST',
    headers,
    body: formData,
    signal,
  })

  const isStreaming = isStreamingAudioResponse(response)

  if (!response.ok) {
    let message = `API Error: ${response.status}`
    try {
      const ct = response.headers.get('content-type') || ''
      if (ct.includes('application/json')) {
        const json = await response.json()
        message = json?.message || json?.error || message
      } else {
        const text = await response.text()
        if (text) message = text
      }
    } catch (e) {
      calculateErrorMessage(e)
    }
    throw new Error(message)
  }

  if (isStreaming) {
    const boundary = extractBoundary(response)
    if (!boundary) {
      return {
        streaming: true,
        error: new Error('Missing or malformed multipart boundary'),
        response,
      }
    }
    return {streaming: true, response, boundary}
  }

  let userTranscription =
    safeDecodeHeader(response.headers.get('X-Transcription-Text')) ||
    safeDecodeHeader(response.headers.get('x-transcription-text')) ||
    safeDecodeHeader(response.headers.get('X-Transcription')) ||
    safeDecodeHeader(response.headers.get('x-transcription')) ||
    safeDecodeHeader(response.headers.get('Transcription'))

  let botAnswerText =
    safeDecodeHeader(response.headers.get('X-Answer')) ||
    safeDecodeHeader(response.headers.get('x-answer')) ||
    safeDecodeHeader(response.headers.get('X-Answer-Text')) ||
    safeDecodeHeader(response.headers.get('x-answer-text')) ||
    safeDecodeHeader(response.headers.get('X-Answer-Test')) ||
    safeDecodeHeader(response.headers.get('x-answer-test')) ||
    safeDecodeHeader(response.headers.get('Answer-Text'))

  let walkthroughData =
    safeDecodeHeader(response.headers.get('X-Walkthrough-Data')) ||
    safeDecodeHeader(response.headers.get('x-walkthrough-data'))

  // Set when the server was not sure what it heard: the audio is a spoken "Kya aap ne pucha: ...?"
  // and nothing was executed. The call simply continues — the user's "haan" runs the question.
  const needsConfirmation =
    (response.headers.get('X-Needs-Confirmation') || '').toLowerCase() === 'true'
  const confidenceHeader = response.headers.get('X-Transcript-Confidence')
  const transcriptConfidence =
    confidenceHeader !== null && Number.isFinite(Number(confidenceHeader))
      ? Number(confidenceHeader)
      : null

  const audio = await response.blob()

  return {
    userTranscription,
    botAnswerText,
    audioBlob: audio,
    walkthroughData,
    needsConfirmation,
    transcriptConfidence,
  }
}
